<script setup lang="ts">
import { ref, type Ref, onMounted, onUnmounted } from 'vue';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import Robflow from '@/components/programming/Robflow.vue';
import VirtualRobotConfig from '@/components/programming/VirtualRobotConfig.vue';
import { getVirtualRobotLoadingStatus } from '@/api/VirtualRobotApi';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();
const virtualRobotToken = ref('');
const error: Ref<string | undefined> = ref(undefined);

// keep track of interval so we can clean it up later
let loadingStatusInterval: ReturnType<typeof setInterval> | null = null;

async function getVirtualRobotToken() {
  error.value = '';
  loadingSpinnerStore.setShowLoadingOverlay(
    true,
    true,
    $gettext('Preparing Cloud RobFlow instance...'),
    'background',
  );

  // manually set progress to 0% at start
  setTimeout(() => {
    loadingSpinnerStore.setLoadingOverlayProgressPercent(0);
  }, 300);

  // clear existing interval before starting new one
  if (loadingStatusInterval) {
    clearInterval(loadingStatusInterval);
  }

  // start polling status
  loadingStatusInterval = setInterval(() => {
    getVirtualRobotLoadingStatus()
      .then((response) => {
        const { status, token } = response.data;
        loadingSpinnerStore.setLoadingOverlayProgressPercent(status);

        if (status === 100) {
          // stop polling once done
          if (loadingStatusInterval) {
            clearInterval(loadingStatusInterval);
            loadingStatusInterval = null;
          }

          virtualRobotToken.value = token;
          setTimeout(() => {
            loadingSpinnerStore.setShowLoadingOverlay(false);
          }, 100);
        }
      })
      .catch((err) => {
        console.error('Error while checking loading status:', err);
        error.value = err;
      });
  }, 1000);
}

onUnmounted(() => {
  if (loadingStatusInterval) {
    clearInterval(loadingStatusInterval);
    loadingStatusInterval = null;
  }
  loadingSpinnerStore.setShowLoadingOverlay(false);
});

onMounted(() => {
  getVirtualRobotToken();
});
</script>

<template>
  <div class="w-100 h-100">
    <robflow :virtual-robot-token="virtualRobotToken" :error="error" />
    <virtual-robot-config v-if="virtualRobotToken" />
  </div>
</template>
